import React from "react";
// Vee Rule Work Flow

// Default
import { useState, useCallback, useEffect, useMemo } from "react";
import {
  addEdge,
  Background,
  ReactFlow,
  Controls,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  Node,
  Edge,
  Connection,
  OnConnect,
  NodeTypes,
  EdgeTypes,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { v4 as uuidv4 } from "uuid";

// Mui
import Stack from "@mui/material/Stack";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import Link from "@mui/material/Link";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

// Mui icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// react-icon

// CodeMirror
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";

// Custom
import Layout from "../../layout";
import RuleBlock from "./RuleBlock";
import DataBlock from "./DataBlock";
import ErrorBlock from "./ErrorBlock";
import TextBlock from "./TextBlock";
import CustomEdge from "./CustomEdge";
import VeeRuleBuilderModal from "../modals/VeeRuleBuilderModal";
import CustomToast from "../toast/CustomToast";
import VeeManageFlowModal from "../modals/VeeManageFlowModal";
import CommonModal from "../modals/CommonModal";
import CustomInput from "../../forms/input/Input";
import CustomDataTable from "../table/CustomTable";
import Spinner from "../spinner/Spinner";
import { ToastState } from "../../types";

// Redux

// Utils
import { fetchGETRequest, fetchPOSTRequest } from "../../utils/services";

// JSON
// import { nodeApiData } from "../../trash/node";
import { ruleOfDataFormater } from "../../utils/helper";
import { CustomeColors } from "../../constants";
import VeeRuleDeleteModal from "../modals/VeeRuleDeleteModel";
import {
  Check,
  Clear,
  West,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import CommonRuleModal from "../modals/CommonRuleModel";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";

// Types
interface RuleTitle {
  name: string;
  value: string;
}

interface RuleConstDataItem {
  title: RuleTitle;
  bg: string;
  rule: any[];
  icon: string;
}

interface WorkFlowItem {
  id: { id: string };
  name: string;
  additionalInfo: { description: string };
  nodes?: any[];
  edges?: any[];
}

interface MetaDataNode {
  id: { id: string };
  name: string;
  type: string;
  additionalInfo: {
    layoutX: number;
    layoutY: number;
    description?: string;
  };
  [key: string]: any;
}

interface MetaDataConnection {
  fromIndex: number;
  toIndex: number;
  type: string;
}

interface MetaData {
  nodes: MetaDataNode[];
  connections: MetaDataConnection[];
  firstNodeIndex: number;
  [key: string]: any;
}

interface CustomNodeData extends Record<string, unknown> {
  ruleName: string;
  addInfo?: {
    bgColor?: string;
    icon?: string;
    action?: string;
    iconUrl?: string;
  };
  jsonData?: any;
  attributes?: any[];
  blockType?: string;
  clazz?: string;
}

interface CustomNode extends Node {
  data: CustomNodeData;
  cmType: string;
}

interface CustomEdgeData extends Record<string, unknown> {
  label?: string;
}

interface CustomEdge extends Edge {
  data?: CustomEdgeData;
}

interface CommonRuleModalData {
  title: string;
  action: string;
  tabs: string[];
  bgColor: string;
  nodeData: any;
  icon: string | null;
  isNew?: boolean;
}

interface RootState {
  vee: {
    veeRuleWorkflow: any[];
  };
}

interface DragParams {
  target: string;
}

interface DeleteModal {
  type: string | null;
  id: string | null;
}

interface NodeTextModal {
  open: boolean;
  message: string;
}

interface CommonRuleModalState {
  open: boolean;
  data: CommonRuleModalData;
}

const actionsTypes = [
  "Device Profile Node",
  "Save Client Attributes",
  "Save Timeseries",
  "Log RPC From Device",
  "Log Other",
  "RPC Call Request",
];

const filterTypes = ["Message Type Switch"];

const RulesConstData: RuleConstDataItem[] = [
  {
    title: {
      name: "Enrichment",
      value: "ENRICHMENT",
    },
    bg: CustomeColors.veeRule.enrichment,
    rule: [],
    icon: "grading",
  },
  {
    title: {
      name: "Filter",
      value: "FILTER",
    },
    bg: CustomeColors.veeRule.filter,
    rule: [],
    icon: "filter_list",
  },
  {
    title: {
      name: "Flow",
      value: "FLOW",
    },
    bg: CustomeColors.veeRule.flow,
    rule: [],
    icon: "settings_ethernet",
  },
  {
    title: {
      name: "Transformation",
      value: "TRANSFORMATION",
    },
    bg: CustomeColors.veeRule.transformation,
    rule: [],
    icon: "crop",
  },
  {
    title: {
      name: "External",
      value: "EXTERNAL",
    },
    bg: CustomeColors.veeRule.external,
    rule: [],
    icon: "cloud_upload",
  },
  {
    title: {
      name: "Action",
      value: "ACTION",
    },
    bg: CustomeColors.veeRule.action,
    rule: [],
    icon: "flash_on",
  },
];

function findAddInfo(mainArray: RuleConstDataItem[], subObj: any): any {
  for (let mainItem of mainArray) {
    for (let rule of mainItem?.rule) {
      if (rule?.clazz === subObj?.type) {
        return {
          bgColor: mainItem?.bg,
          icon: mainItem?.icon,
          action: mainItem?.title?.name,
        };
      }
    }
  }
  return { bgColor: "gray", icon: "", action: "" };
}

function replaceNullsWithEmptyStrings(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(replaceNullsWithEmptyStrings);
  } else if (obj !== null && typeof obj === "object") {
    return Object.entries(obj).reduce((acc: any, [key, value]) => {
      acc[key] = value === null ? "" : replaceNullsWithEmptyStrings(value);
      return acc;
    }, {});
  }
  return obj;
}

const DummyRestAPiCallData = {
  id: {
    entityType: "RULE_NODE",
    id: "",
  },
  createdTime: 1745991043484,
  ruleChainId: {
    entityType: "RULE_CHAIN",
    id: "a3358fd0-03d1-11f0-9f1a-2db71be85a80",
  },
  type: "org.thingsboard.rule.engine.rest.TbRestApiCallNode",
  name: "", // change
  debugSettings: {
    failuresEnabled: true,
    allEnabled: false,
    allEnabledUntil: 1742285679257,
  },
  singletonMode: false,
  queueName: "",
  configurationVersion: 3,
  externalId: "",
  configuration: {
    restEndpointUrlPattern: "",
    requestMethod: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    useSimpleClientHttpFactory: false,
    readTimeoutMs: 0,
    maxParallelRequestsCount: 0,
    parseToPlainText: false,
    enableProxy: false,
    useSystemProxyProperties: false,
    proxyHost: null,
    proxyPort: 0,
    proxyUser: null,
    proxyPassword: null,
    proxyScheme: null,
    credentials: {
      type: "anonymous",
    },
    ignoreRequestBody: false,
    maxInMemoryBufferSizeInKb: 256,
  },
  additionalInfo: {
    description: "",
    layoutX: 0, // change
    layoutY: 0, // change
  },
};

const DummyNodeData = {
  name: "", // change
  isDummy: true,
  id: { id: "" },
};

const VeeRuleWorkflow: React.FC = () => {
  const NEWID = uuidv4();
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [originalNodes, setOriginalNodes] = useState<Node[]>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [modalDelete, setModalDelete] = useState<DeleteModal>({
    type: null,
    id: null,
  });
  const [flowManagementModalOpen, setFlowManagementModalOpen] =
    useState<boolean>(false);
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "",
  });
  const [saveModal, setSaveModal] = useState<boolean>(false);
  const [flowName, setFlowName] = useState<string>("");
  const [allWorkFlow, setAllWorkFlow] = useState<WorkFlowItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [saveLoader, setSaveLoader] = useState<boolean>(false);
  const [selectedWorkFlow, setSelectedWorkFlow] = useState<WorkFlowItem | null>(
    null
  );
  const [metaData, setMetaData] = useState<MetaData | null>(null);
  const [nodeTextModal, setNodeTextModal] = useState<NodeTextModal>({
    open: false,
    message: "",
  });

  const [commonRuleModel, setCommonRuleModel] = useState<CommonRuleModalState>({
    open: false,
    data: {
      title: "",
      action: "",
      tabs: ["1"],
      bgColor: "",
      nodeData: null,
      icon: null,
    },
  });
  const [editedCodeBlock, setEditedCodeBlock] = useState<string>("");

  const [nodeRuleOfData, setNodeRuleOfData] =
    useState<RuleConstDataItem[]>(RulesConstData);

  const { veeRuleWorkflow } = useAppSelector((state) => state.vee);

  const dispatch = useAppDispatch();

  const shouldAddLabel = (params: DragParams): string | null => {
    switch (params.target.slice(0, params.target.length - 2)) {
      case "Message Type Switch":
        return "Success";
      case "Save Client Attributes":
        return "Post attributes";
      case "Save Timeseries":
        return "Post telemetry";
      case "Log RPC From Device":
        return "RPC Request from Device";
      case "Log Other":
        return "Other";
      case "RPC Call Request":
        return "RPC Request to Device";
      default:
        return null;
    }
  };

  const onConnect: OnConnect = useCallback(
    (params: Connection) =>
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            // type: "custom",
            // data: { label: shouldAddLabel(params) || undefined },
          },
          eds as Edge[]
        )
      ),
    [setEdges]
  );

  const [expanded, setExpanded] = useState<number | null>(null);

  const handleExpansion = (panel: number) => {
    setExpanded(expanded === panel ? null : panel);
  };

  useEffect(() => {
    if (nodes?.length === 0) {
      setNodes([
        {
          id: "Input_0",
          type: "textBlock",
          position: { x: 100, y: 20 },
          data: {
            ruleName: "Input",
            addInfo: {
              bgColor: `${CustomeColors.veeRule.input}`,
              icon: "exit_to_app",
            },
          },
        } as CustomNode,
      ]);
    }
    fetchVeeData();
    fetchApiRuleData();
  }, []);

  const fetchVeeData = async () => {
    setLoading(true);
    try {
      const response = await fetchGETRequest(
        `rulechains?pageSize=10&page=0&sortProperty=createdTime&sortOrder=DESC&type=CORE`,
        {},
        true
      );
      if (response?.data?.data?.length > 0) {
        setAllWorkFlow([...response?.data?.data]);
      }
    } catch (error) {
      return error;
    } finally {
      setLoading(false);
    }
  };

  const fetchApiRuleData = async () => {
    try {
      const response = await fetchGETRequest(
        `rulenodes?componentTypes=FILTER,ENRICHMENT,TRANSFORMATION,ACTION,EXTERNAL,FLOW&ruleChainType=CORE`,
        {}
      );
      if (response?.nodes) {
        const FormatedData = ruleOfDataFormater(
          RulesConstData,
          response?.nodes
        );
        setNodeRuleOfData(FormatedData as RuleConstDataItem[]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Handle dropping new blocks
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const reactFlowBounds = (
        event.target as HTMLElement
      ).getBoundingClientRect();
      const dropData = event.dataTransfer.getData("application/@xyflow/react");

      const jsonData = JSON.parse(dropData);

      const type = jsonData?.value;
      const bgColor = jsonData?.bgColor;
      const clazz = jsonData?.clazz;

      if (!type) return;

      const position = {
        x: event.clientX - reactFlowBounds.left - 40,
        y: event.clientY - reactFlowBounds.top + 20,
      };

      const getData = (type: string): CustomNodeData => {
        if (type === "dataBlock") {
          return {
            ruleName: "Data Block",
            jsonData: {},
            attributes: [],
            blockType: "Data Input Block",
            clazz,
          };
        } else if (type === "errorBlock") {
          return {
            ruleName: "Exception Block",
            clazz,
          };
        } else if (filterTypes.includes(type)) {
          return {
            ruleName: type,
            addInfo: { bgColor },
            clazz,
          };
        } else if (actionsTypes.includes(type)) {
          return {
            ruleName: type,
            addInfo: { bgColor },
            clazz,
          };
        } else {
          return {
            ruleName: type,
            attributes: [],
            addInfo: {
              bgColor,
              icon: jsonData?.icon,
              iconUrl: jsonData?.iconUrl,
            },
            clazz,
          };
        }
      };

      const newNode: CustomNode = {
        id: NEWID,
        cmType:
          type === "dataBlock"
            ? "dataBlock"
            : actionsTypes.includes(type) || filterTypes.includes(type)
            ? "textBlock"
            : "ruleBlock",
        type:
          type === "dataBlock"
            ? "dataBlock"
            : type === "errorBlock"
            ? "errorBlock"
            : actionsTypes.includes(type) || filterTypes.includes(type)
            ? "textBlock"
            : "ruleBlock",
        position,
        data: getData(type),
      };

      if (clazz == "org.thingsboard.rule.engine.rest.TbRestApiCallNode") {
        const DATA = { ...DummyRestAPiCallData };
        DATA.name = type;
        DATA.id.id = NEWID;
        DATA.additionalInfo = {
          ...DATA.additionalInfo,
          layoutX: position.x,
          layoutY: position.y,
        };
        handleChangeMetaData(DATA, true, false);
      }

      setNodes((nds) => [...nds, newNode as Node]);
    },
    [nodes, setNodes]
  );

  // Handle node click to open modal for editing
  const onNodeClick = (_: React.MouseEvent, node: Node) => {
    // console.log(node, "***click***");

    if (!metaData?.nodes) {
      console.warn("metaData is null at the time of click");
    }

    const nodeData = node.data as CustomNodeData;

    const clickedNode = metaData?.nodes?.find((item: MetaDataNode) => {
      return item?.name === nodeData?.ruleName;
    });

    // console.log(clickedNode, "clicknode--");

    if (clickedNode) {
      setCommonRuleModel({
        open: true,
        data: {
          action: nodeData?.addInfo?.action || "",
          title: nodeData?.ruleName || "",
          icon: nodeData?.addInfo?.icon || null,
          nodeData: clickedNode,
          bgColor: nodeData?.addInfo?.bgColor || "",
          tabs: ["1"],
          isNew: false,
        },
      });
    } else {
      if (
        nodeData.clazz == "org.thingsboard.rule.engine.rest.TbRestApiCallNode"
      ) {
        const DATA = { ...DummyRestAPiCallData };
        DATA.name = nodeData?.ruleName;
        DATA.additionalInfo = {
          ...DATA.additionalInfo,
          layoutX: node?.position?.x || 0,
          layoutY: node?.position?.y || 0,
        };
        DATA.id.id = uuidv4();
        setCommonRuleModel({
          open: true,
          data: {
            action: nodeData?.addInfo?.action || "",
            title: nodeData?.ruleName || "",
            icon: nodeData?.addInfo?.icon || null,
            nodeData: DATA,
            bgColor: nodeData?.addInfo?.bgColor || "",
            tabs: ["1"],
            isNew: true,
          },
        });
      } else {
        const DATA = { ...DummyNodeData };
        DATA.id.id = uuidv4();
        DATA.name = nodeData?.ruleName;
        setCommonRuleModel({
          open: true,
          data: {
            action: nodeData?.addInfo?.action || "",
            title: nodeData?.ruleName || "",
            icon: nodeData?.addInfo?.icon || null,
            nodeData: DATA,
            bgColor: nodeData?.addInfo?.bgColor || "",
            tabs: ["1"],
            isNew: true,
          },
        });
      }
    }
  };

  // Update node data
  const updateNode = (updatedNode: CustomNode) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === updatedNode.id
          ? { ...node, data: { ...updatedNode.data } }
          : node
      )
    );
  };

  // Update DataBlock JSON data
  const updateNodeData = (nodeId: string, newJsonData: any) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, jsonData: newJsonData } }
          : node
      )
    );
  };

  // Close modal
  const closeModal = () => {
    setSelectedNode(null);
    setModalIsOpen(false);
  };

  // Handle deletion via keyboard
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (
        !modalIsOpen &&
        (event.key === "Delete" || event.key === "Backspace")
      ) {
        setNodes((nds) => nds.filter((node) => !node.selected));
        setEdges((eds) => eds.filter((edge) => !edge.selected));
      }
    },
    [setNodes, setEdges, modalIsOpen]
  );

  // Manual delete button handler
  const deleteSelected = () => {
    setNodes((nds) => nds.filter((node) => !node.selected));
    setEdges((eds) => eds.filter((edge) => !edge.selected));
  };

  const deleteNodeWithId = (ID: string) => {
    setNodes((nds) => nds.filter((node) => node.id !== ID));
    setEdges((eds) => eds.filter((edge) => edge.id !== ID));

    const deletedNode = metaData?.nodes?.findIndex(
      (row: MetaDataNode) => row.id.id === ID
    );

    if (deletedNode !== undefined && deletedNode > -1) {
      const connections = metaData?.connections?.filter(
        (cont: MetaDataConnection) =>
          cont.fromIndex != deletedNode && cont.toIndex != deletedNode
      );

      const nodes = [
        ...(metaData?.nodes?.slice(0, deletedNode) || []),
        ...(metaData?.nodes?.slice(deletedNode + 1) || []),
      ];
      setMetaData((prev) =>
        prev ? { ...prev, connections: connections || [], nodes } : null
      );
    }
  };

  const handleSaveFlow = async () => {
    try {
      setSaveLoader(true);

      const copyNodes = [...nodes];
      copyNodes.shift();

      const nodeIndexMap: { [key: string]: number } = {};
      copyNodes.forEach((node, index) => {
        nodeIndexMap[node.id] = index;
      });

      const transformedEdges = edges
        .filter(
          (edge) => edge.source !== "Input_0" && edge.target !== "Input_0"
        )
        .map((edge) => {
          const fromIndex = nodeIndexMap[edge.source!];
          const toIndex = nodeIndexMap[edge.target!];

          // Only include if both indices are valid
          if (fromIndex === undefined || toIndex === undefined) return null;

          return {
            fromIndex,
            toIndex,
            type: (edge.data as CustomEdgeData)?.label || "Success",
          };
        })
        .filter(Boolean);

      const updatedNodes = new Map(copyNodes.map((tb) => [tb.id, tb.position]));

      const updatedMetaData = metaData?.nodes?.map((node: MetaDataNode) => {
        if (updatedNodes.has(node?.id?.id)) {
          const position = updatedNodes.get(node.id.id);
          if (position) {
            node.additionalInfo.layoutX = position.x;
            node.additionalInfo.layoutY = position.y;
          }
          node.additionalInfo.description =
            node?.additionalInfo?.description ?? "";
        }

        const { ruleChainId, externalId, createdTime, ...rest } = node;
        return rest;
      });

      const cleanedNodeData = replaceNullsWithEmptyStrings(updatedMetaData);

      const updatedPayload = {
        ...metaData!,
        nodes: cleanedNodeData,
        connections: transformedEdges,
      };

      delete (updatedPayload as any).ruleChainConnections;

      const response = await fetchPOSTRequest(
        `ruleChain/metadata`,
        updatedPayload
      );

      setToast({
        message: `Flow saved successfully!`,
        severity: "success",
      });
    } catch (error: any) {
      setToast({
        message: error?.response?.data?.message || "Error saving flow",
        severity: "error",
      });
    } finally {
      setSaveLoader(false);
      setSaveModal(false);
      setFlowName("");
    }
  };

  // Load an existing flow
  const handleLoadFlow = (flow: WorkFlowItem) => {
    setNodes(flow?.nodes || []);
    setEdges(flow?.edges || []);
    setFlowManagementModalOpen(false);
  };

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData("application/@xyflow/react", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const DragContent = ({
    title,
    value,
    bgColor,
    details,
    icon,
    clazz,
  }: {
    title: string;
    value: string;
    bgColor: string;
    details: any;
    icon: string;
    clazz: string;
  }) => {
    const Icon = details?.icon ? details?.icon : icon;

    return (
      <div
        onDragStart={(event) =>
          onDragStart(
            event,
            JSON.stringify({
              value,
              bgColor,
              icon: Icon,
              iconUrl: details?.iconUrl,
              clazz,
            })
          )
        }
        draggable
        style={{
          padding: "10px",
          color: "#fff",
          backgroundColor: bgColor,
          borderRadius: "5px",
          cursor: "grab",
          textAlign: "center",
          alignItems: "center",
          display: "flex",
          position: "relative",
          gap: 8,
          marginLeft: 8,
          marginRight: 8,
        }}>
        {details?.iconUrl ? (
          <img
            src={details?.iconUrl}
            alt="icon"
            style={{ width: 18, height: 18 }}
          />
        ) : (
          <span className="material-icons" style={{ fontSize: 18 }}>
            {Icon}
          </span>
        )}

        {details?.inEnabled && (
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 5,
              border: "2px solid gray",
              position: "absolute",
              left: -8,
              backgroundColor: "lightgray",
              opacity: 0.7,
            }}
          />
        )}

        {details?.outEnabled && (
          <div
            style={{
              width: 12,
              height: 12,
              borderRadius: 5,
              border: "2px solid gray",
              position: "absolute",
              right: -8,
              backgroundColor: "lightgray",
              opacity: 0.7,
            }}
          />
        )}

        <p
          style={{
            color: "#ffffff",
            fontSize: "14px",
            fontWeight: 500,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}>
          {title}
        </p>
      </div>
    );
  };

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      ruleBlock: (props: any) => (
        <RuleBlock
          {...props}
          onEdit={() => {
            onNodeClick(undefined as any, props);
          }}
          onDelete={() => {
            setModalDelete({ type: props?.data?.ruleName, id: props.id });
          }}
        />
      ),
      dataBlock: (props: any) => (
        <DataBlock {...props} selectedNode={selectedNode} />
      ),
      errorBlock: (props: any) => (
        <ErrorBlock {...props} selectedNode={selectedNode} />
      ),
      textBlock: (props: any) => (
        <TextBlock
          {...props}
          selectedNode={selectedNode}
          onEdit={() => {
            onNodeClick(undefined as any, props);
          }}
          onDelete={() => {
            setModalDelete({ type: props?.data?.ruleName, id: props.id });
          }}
        />
      ),
    }),
    [selectedNode, metaData]
  );

  const edgeTypes: EdgeTypes = useMemo(
    () => ({
      custom: CustomEdge as any,
    }),
    []
  );

  const isNodeChanged = (): boolean => {
    const cleanNodes = (nodes: Node[]) => {
      return nodes?.map(({ id, type, data, position }) => ({
        id,
        type,
        data,
        position,
      }));
    };

    return (
      JSON.stringify(cleanNodes(nodes)) !==
      JSON.stringify(cleanNodes(originalNodes))
    );
  };

  const handleSaveMetaData = (
    metaDataObj: MetaData,
    saveNew: boolean,
    setInNodes: boolean
  ) => {
    const DATA = metaDataObj;

    setMetaData(DATA);

    const updatedNodesArray: CustomNode[] = [];
    const updatedEdgesArray: CustomEdge[] = [];

    DATA?.nodes?.map((item: MetaDataNode) => {
      const addInfoData = findAddInfo(nodeRuleOfData, item);

      return updatedNodesArray.push({
        id: item?.id?.id,
        cmType: "textBlock",
        type: "textBlock",
        position: {
          x: item?.additionalInfo?.layoutX,
          y: item?.additionalInfo?.layoutY,
        },
        data: {
          ruleName: item?.name,
          addInfo: { ...addInfoData },
        },
      } as CustomNode);
    });

    DATA?.connections?.map((connection: MetaDataConnection) => {
      return updatedEdgesArray.push({
        id: `${updatedNodesArray?.[connection?.fromIndex]?.id}__${
          updatedNodesArray?.[connection?.toIndex]?.id
        }`,
        target: updatedNodesArray?.[connection?.toIndex]?.id,
        source: updatedNodesArray?.[connection?.fromIndex]?.id,
        type: "custom",
        data: { label: connection?.type },
      } as CustomEdge);
    });

    updatedNodesArray.unshift({
      id: "Input_0",
      cmType: "textBlock",
      type: "textBlock",
      position: { x: 87, y: 164 },
      data: {
        ruleName: "Input",
        addInfo: {
          bgColor: `${CustomeColors.veeRule.input}`,
          icon: "exit_to_app",
        },
      },
    } as CustomNode);

    const idx = DATA?.nodes[DATA?.firstNodeIndex]?.id?.id;
    updatedEdgesArray.unshift({
      id: `Input_0__${idx}`,
      target: DATA?.nodes?.[DATA?.firstNodeIndex]?.id?.id,
      source: "Input_0",
      type: "custom",
      data: undefined,
    } as CustomEdge);

    if (saveNew) {
      setOriginalNodes(JSON.parse(JSON.stringify(updatedNodesArray)));
    }

    if (setInNodes) {
      setNodes(updatedNodesArray as Node[]);
      setEdges(updatedEdgesArray as Edge[]);
    }
  };

  const handleChangeMetaData = (
    updatedNode: any,
    isNew: boolean,
    setInNodes: boolean
  ) => {
    const copyNodes = [...nodes];
    copyNodes.shift();

    updatedNode = JSON.parse(JSON.stringify(updatedNode));

    const nodeIndexMap: { [key: string]: number } = {};
    copyNodes.forEach((node, index) => {
      nodeIndexMap[node.id] = index;
    });

    const transformedEdges = edges
      .filter((edge) => edge.source !== "Input_0" && edge.target !== "Input_0")
      .map((edge) => {
        const fromIndex = nodeIndexMap[edge.source!];
        const toIndex = nodeIndexMap[edge.target!];

        // Only include if both indices are valid
        if (fromIndex === undefined || toIndex === undefined) return null;

        return {
          fromIndex,
          toIndex,
          type: (edge.data as CustomEdgeData)?.label || "Success",
        };
      })
      .filter(Boolean);

    const updatedNodes = new Map(copyNodes.map((tb) => [tb.id, tb.position]));

    const updatedMetaData = metaData?.nodes?.map((node: MetaDataNode) => {
      let newNode = node;

      if (newNode.id.id === updatedNode.id.id && !isNew) {
        newNode = updatedNode;
      }
      if (updatedNodes.has(newNode?.id?.id)) {
        const position = updatedNodes.get(node.id.id);
        if (position) {
          newNode.additionalInfo.layoutX = position.x;
          newNode.additionalInfo.layoutY = position.y;
        }
      }
      return newNode;
    });

    if (isNew) {
      updatedMetaData?.push(updatedNode);
    }

    const updatedMain = {
      ...metaData!,
      nodes: updatedMetaData || [],
      connections: transformedEdges as MetaDataConnection[],
    };

    handleSaveMetaData(updatedMain, false, setInNodes);
    setCommonRuleModel({
      open: false,
      data: {
        title: "",
        action: "",
        tabs: ["1"],
        bgColor: "",
        nodeData: null,
        icon: null,
      },
    });
  };

  const handleSelectedWorkFlow = async (payload: WorkFlowItem) => {
    setLoading(true);
    try {
      setSelectedWorkFlow(payload);
      const response = await fetchGETRequest(
        `ruleChain/${payload?.id?.id}/metadata`,
        {},
        true
      );

      if (response?.data?.nodes?.length > 0) {
        handleSaveMetaData(response?.data, true, true);
      }
    } catch (error: any) {
      setToast({
        message: error?.message || "Error loading workflow",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedWorkFlow(null);
    setNodes([]);
    setEdges([]);
  };

  const rowsContent = allWorkFlow?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody">
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.id?.id || "-"}
        </Typography>
      </TableCell>

      <TableCell sx={{ cursor: "pointer" }}>
        <Box
          className="custom_table_tbody_cell"
          onClick={() => handleSelectedWorkFlow(item)}>
          <Link color="#1280E7">{item?.name || "-"}</Link>
        </Box>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.additionalInfo?.description || "-"}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      <CustomToast
        open={toast.message}
        severity={toast.severity}
        setToast={setToast}
      />

      {loading && (
        <Box mt={"20px"}>
          <Spinner />
        </Box>
      )}

      {allWorkFlow?.length > 0 && !loading && !selectedWorkFlow && (
        <Card sx={{ maxHeight: "600px" }}>
          <CardHeader
            title={
              <Stack>
                <Typography variant="h5">VEE Engine Rules</Typography>
                <Typography variant="caption" color="#7D808F" mt={"8px"}>
                  Manage and configure your Validation, Estimation and Editing
                  rules
                </Typography>
              </Stack>
            }
          />
          <Divider />

          <CardContent>
            <CustomDataTable
              columns={["ID", "Rule Name", "Description"]}
              pagination={false}
              rowsContent={rowsContent}
            />
          </CardContent>
        </Card>
      )}

      {selectedWorkFlow && !loading && (
        <ReactFlowProvider>
          <Card>
            <div
              style={{
                display: "flex",
                height: "77vh",
              }}
              tabIndex={0}
              onKeyDown={handleKeyDown as any}>
              <Box
                sx={{
                  width: 240,
                  px: "15px",
                  py: "5px",
                  backgroundColor: "#f0f0f0",
                  borderRight: "1px solid #ddd",
                  overflowY: "auto",
                }}>
                <IconButton onClick={handleBack} sx={{ p: 0.5 }}>
                  <West />
                </IconButton>
                {nodeRuleOfData.map((item, index) => (
                  <Accordion
                    key={index}
                    expanded={expanded === index}
                    onChange={() => handleExpansion(index)}
                    sx={{
                      boxShadow: "none",
                      background: "#f0f0f0",
                      borderBottom: "1px solid #ddd",
                      "&:before": { display: "none" },
                      borderRadius: 0,
                    }}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      id={item?.title?.value}
                      sx={{
                        padding: 0,
                        minHeight: "48px",
                        "& .MuiAccordionSummary-content": { margin: 0 },
                      }}>
                      {item.icon && (
                        <span
                          className="material-icons"
                          style={{
                            marginLeft: 5,
                            marginRight: 8,
                            fontSize: 20,
                          }}>
                          {item.icon}
                        </span>
                      )}
                      <Typography variant="body1" fontWeight={500}>
                        {item?.title?.name}
                      </Typography>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{ padding: "0 0 12px 0", background: "#f0f0f0" }}>
                      <Stack direction={"column"} gap={"8px"}>
                        {item?.rule?.map((rrr: any, index: number) => {
                          return (
                            <DragContent
                              key={index}
                              title={rrr?.label}
                              value={rrr?.value}
                              bgColor={item?.bg}
                              details={rrr?.details}
                              icon={item.icon}
                              clazz={rrr?.clazz}
                            />
                          );
                        })}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
              <div
                style={{
                  flex: 1,
                  height: "100%",
                  position: "relative",
                }}
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}>
                {isNodeChanged() && (
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "20px",
                      right: "10px",
                      zIndex: 10,
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                    }}>
                    <IconButton
                      onClick={() => handleSaveFlow()}
                      sx={{
                        color: "white",
                        background: "#FF4D4D",
                        padding: "5px",
                        "&:hover": {
                          background: "#FF7F7F",
                        },
                      }}>
                      <Check style={{ fontSize: 26 }} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setNodes(originalNodes);
                      }}
                      sx={{
                        color: "white",
                        background: "#FF4D4D",
                        padding: "5px",
                        "&:hover": {
                          background: "#FF7F7F",
                        },
                      }}>
                      <Clear style={{ fontSize: 26 }} />
                    </IconButton>
                  </Box>
                )}

                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  nodeTypes={nodeTypes}
                  fitView
                  edgeTypes={edgeTypes}>
                  <Controls />
                  <Background />
                </ReactFlow>
              </div>
            </div>
          </Card>

          <VeeRuleBuilderModal
            node={selectedNode}
            onSave={updateNode}
            onClose={closeModal}
            setToast={setToast}
            modalIsOpen={modalIsOpen && !!selectedNode}
          />

          <VeeRuleDeleteModal
            onClose={() => {
              setModalDelete({ id: null, type: null });
            }}
            onDelete={() => {
              if (modalDelete.id) {
                deleteNodeWithId(modalDelete.id);
              }
              setModalDelete({ id: null, type: null });
            }}
            type={modalDelete.type}
          />

          <VeeManageFlowModal
            onLoadFlow={handleLoadFlow}
            onClose={() => setFlowManagementModalOpen(false)}
            setToast={setToast}
            open={flowManagementModalOpen}
          />

          <CommonModal
            title="Code Editor"
            open={nodeTextModal?.open}
            onClose={() => {
              setNodeTextModal({
                open: false,
                message: "",
              });
              setEditedCodeBlock("");
            }}
            handleSave={() => {}}
            loading={false}
            body={
              <CodeMirror
                value={nodeTextModal?.message || ""}
                height="400px"
                extensions={[javascript()]}
                theme="dark"
                editable={true}
                style={{
                  borderRadius: "5px",
                  fontSize: "14px",
                  fontFamily: "monospace",
                  backgroundColor: "#282c34",
                  color: "#61dafb",
                  overflow: "hidden",
                }}
                onChange={(e) => {
                  setEditedCodeBlock(e);
                }}
              />
            }
            textModal={true}
            showEditButton={!!editedCodeBlock}
          />

          <CommonModal
            title={"Save Flow"}
            open={saveModal}
            onClose={() => setSaveModal(false)}
            handleSave={() => handleSaveFlow()}
            loading={saveLoader}
            body={
              <CustomInput
                label={"Enter a name for this flow"}
                value={flowName}
                onChange={(e) => setFlowName(e.target.value)}
                required
              />
            }
            showEditButton={true}
          />

          <CommonRuleModal
            modalIsOpen={commonRuleModel.open}
            onClose={() => {
              setCommonRuleModel({
                open: false,
                data: {
                  title: "",
                  action: "",
                  tabs: ["1"],
                  bgColor: "",
                  nodeData: null,
                  icon: null,
                },
              });
            }}
            onSave={(data: any, isNew: boolean) => {
              handleChangeMetaData(data, isNew, true);
            }}
            data={commonRuleModel.data}
          />
        </ReactFlowProvider>
      )}
    </Layout>
  );
};

export default VeeRuleWorkflow;
